import { jsPDF } from "jspdf";
import "jspdf-autotable";
import api from './api';

/**
 * Serviço para geração de relatórios PDF utilizando jsPDF
 */
class PDFGenerator {
  constructor() {
    this.doc = new jsPDF();
    this.margin = 15;
    this.pageWidth = this.doc.internal.pageSize.width;
    this.pageHeight = this.doc.internal.pageSize.height;
  }

  /**
   * Verifica se há espaço suficiente na página atual
   * @param {number} currentY - Posição Y atual
   * @param {number} requiredSpace - Espaço necessário
   * @returns {number} - Nova posição Y (mesma página ou nova página)
   */
  verificarEspaco(currentY, requiredSpace = 40) {
    if (currentY + requiredSpace > this.pageHeight - 45) {
      this.doc.addPage();
      return this.margin;
    }
    return currentY;
  }

  /**
   * Gera um relatório do dashboard de fornecedores
   * @param {string} clienteId - ID do cliente
   * @param {Object} filtros - Filtros para a busca de fornecedores (opcional)
   * @returns {jsPDF} - Documento PDF gerado
   */
  async gerarRelatorioDashboard(clienteId, filtros = {}) {
    try {
      let nomeCliente = this.obterNomeCliente(filtros);
      
      const [statsResponse, fornecedoresResponse, maturidadeResponse, planosAcaoResponse] = 
        await this.buscarDadosDashboard(clienteId, filtros);
      
      const stats = statsResponse.data;
      const fornecedores = this.extrairFornecedores(fornecedoresResponse);
      const maturidade = maturidadeResponse.data;
      const planosAcao = planosAcaoResponse.data;
      
      let tentativasData = null;
      if (filtros.opcoes?.incluirTentativasEnvio !== false) {
        try {
          const tentativasResponse = await api.get('/dashboard/governanca/fornecedores/tentativas-envio', {
            params: { cliente_id: clienteId }
          });
          tentativasData = tentativasResponse.data;
        } catch (error) {
          // Falha silenciosa se tentativas não estiverem disponíveis
        }
      }
      
      this.doc = new jsPDF();
      
      let currentY = this.criarCabecalho(nomeCliente);
      
      if (filtros && Object.keys(filtros).length > 0) {
        this.adicionarInformacoesFiltros(filtros);
        currentY += 12;
      }
      
      if (stats && (!filtros.opcoes || filtros.opcoes.incluirEstatisticas !== false)) {
        currentY = this.renderizarEstatisticasGerais(stats, currentY);
        currentY += 8;
      }
      
      if (maturidade && (!filtros.opcoes || filtros.opcoes.incluirMaturidade !== false)) {
        currentY = this.renderizarMaturidade(maturidade, currentY);
        currentY += 8;
      }
      
      if (planosAcao && (!filtros.opcoes || filtros.opcoes.incluirPlanosDeAcao !== false)) {
        currentY = this.renderizarEstatisticasPlanosAcao(planosAcao, currentY);
        currentY += 8;
      }
      
      if (!filtros.opcoes || filtros.opcoes.incluirTabela !== false) {
        this.renderizarTabelaFornecedores(fornecedores, currentY);
      }
      
      this.adicionarRodape();
      
      return this.doc;
    } catch (error) {
      throw new Error('Falha ao gerar relatório PDF. ' + (error.response?.data?.message || error.message));
    }
  }

  /**
   * Obter nome do cliente de múltiplas fontes
   */
  obterNomeCliente(filtros) {
    let nomeCliente = filtros.nomeCliente || "Cliente";
    
    if (nomeCliente === "Cliente") {
      try {
        const currentClienteString = localStorage.getItem('currentCliente');
        if (currentClienteString) {
          const currentCliente = JSON.parse(currentClienteString);
          if (currentCliente) {
            nomeCliente = currentCliente.razao_social || currentCliente.nome || nomeCliente;
          }
        }
      } catch (error) {
        // Falha silenciosa ao acessar localStorage
      }
    }
    
    if (nomeCliente === "Cliente" && typeof window !== 'undefined' && window.currentClienteName) {
      nomeCliente = window.currentClienteName;
    }
    
    return nomeCliente;
  }

  /**
   * Buscar dados das APIs necessárias
   */
  async buscarDadosDashboard(clienteId, filtros) {
    const statsPromise = api.get('/dashboard/governanca/fornecedores/stats', {
      params: { cliente_id: clienteId }
    });
    
    const paramsConsulta = {
      cliente_id: clienteId,
      limit: 1000,
      all: true
    };
    
    if (filtros.search) paramsConsulta.search = filtros.search;
    if (filtros.dataInicio) paramsConsulta.data_inicio = filtros.dataInicio;
    if (filtros.dataFim) paramsConsulta.data_fim = filtros.dataFim;
    
    const fornecedoresPromise = api.get('/dashboard/governanca/fornecedores', {
      params: paramsConsulta
    });
    
    const maturidadePromise = api.get('/dashboard/governanca/fornecedores/maturidade', {
      params: { 
        cliente_id: clienteId,
        fornecedor_id: filtros.fornecedorId
      }
    });
    
    const planosAcaoPromise = filtros.opcoes?.incluirPlanosDeAcao !== false 
      ? api.get('/dashboard/governanca/fornecedores/planos-acao', {
          params: { 
            cliente_id: clienteId,
            fornecedor_id: filtros.fornecedorId
          }
        }).catch(() => ({ data: null }))
      : Promise.resolve({ data: null });
    
    return await Promise.all([statsPromise, fornecedoresPromise, maturidadePromise, planosAcaoPromise]);
  }

  /**
   * Extrair fornecedores da resposta da API
   */
  extrairFornecedores(fornecedoresResponse) {
    let fornecedores = [];
    
    if (fornecedoresResponse && fornecedoresResponse.data) {
      if (fornecedoresResponse.data.data && Array.isArray(fornecedoresResponse.data.data)) {
        fornecedores = fornecedoresResponse.data.data;
      } else if (Array.isArray(fornecedoresResponse.data)) {
        fornecedores = fornecedoresResponse.data;
      } else if (fornecedoresResponse.data.items && Array.isArray(fornecedoresResponse.data.items)) {
        fornecedores = fornecedoresResponse.data.items;
      }
    }
    
    return fornecedores;
  }

  /**
   * Criar cabeçalho do documento
   */
  criarCabecalho(nomeCliente) {
    this.doc.setFontSize(12);
    this.doc.setFont("helvetica", "bold");
    
    const nomeTratado = nomeCliente.trim();
    
    if (nomeTratado.length > 25) {
      this.doc.text("Jornada de Conformidade:", this.pageWidth / 2, this.margin + 12, { 
        align: "center" 
      });
      
      const larguraMaxima = this.pageWidth - (this.margin * 2);
      const linhasNome = this.doc.splitTextToSize(nomeTratado, larguraMaxima);
      
      let yPosNome = this.margin + 22;
      
      linhasNome.forEach((linha, index) => {
        this.doc.text(linha, this.pageWidth / 2, yPosNome + (index * 10), {
          align: "center" 
        });
      });
      
      const yFinal = yPosNome + (linhasNome.length * 10) + 6;
      
      const dataAtual = new Date().toLocaleDateString("pt-BR");
      const horaAtual = new Date().toLocaleTimeString("pt-BR");
      
      this.doc.setFontSize(10);
      this.doc.setFont("helvetica", "normal");
      this.doc.text(`Gerado em: ${dataAtual} - ${horaAtual}`, this.pageWidth - this.margin, yFinal, { 
        align: "right" 
      });
      
      this.doc.setDrawColor(0, 0, 0);
      this.doc.line(this.margin, yFinal + 4, this.pageWidth - this.margin, yFinal + 4);
      
      return yFinal + 8;
      
    } else {
      const tituloCompleto = `Jornada de Conformidade: ${nomeTratado}`;
      const larguraTitulo = this.doc.getStringUnitWidth(tituloCompleto) * 14 / this.doc.internal.scaleFactor;
      
      if (larguraTitulo > (this.pageWidth - this.margin * 2)) {
        this.doc.text("Jornada de Conformidade:", this.pageWidth / 2, this.margin + 12, { 
          align: "center" 
        });
        this.doc.text(nomeTratado, this.pageWidth / 2, this.margin + 24, {
          align: "center" 
        });
        
        const dataAtual = new Date().toLocaleDateString("pt-BR");
        const horaAtual = new Date().toLocaleTimeString("pt-BR");
        
        this.doc.setFontSize(10);
        this.doc.setFont("helvetica", "normal");
        this.doc.text(`Gerado em: ${dataAtual} - ${horaAtual}`, this.pageWidth - this.margin, this.margin + 32, {
          align: "right" 
        });
        
        this.doc.setDrawColor(0, 0, 0);
        this.doc.line(this.margin, this.margin + 36, this.pageWidth - this.margin, this.margin + 36);
        
        return this.margin + 40;
      } else {
        this.doc.text(tituloCompleto, this.pageWidth / 2, this.margin + 15, { 
          align: "center" 
        });
        
        const dataAtual = new Date().toLocaleDateString("pt-BR");
        const horaAtual = new Date().toLocaleTimeString("pt-BR");
        
        this.doc.setFontSize(10);
        this.doc.setFont("helvetica", "normal");
        this.doc.text(`Gerado em: ${dataAtual} - ${horaAtual}`, this.pageWidth - this.margin, this.margin + 25, { 
          align: "right" 
        });
        
        this.doc.setDrawColor(0, 0, 0);
        this.doc.line(this.margin, this.margin + 30, this.pageWidth - this.margin, this.margin + 30);
        
        return this.margin + 35;
      }
    }
  }

  /**
   * Formatar título com quebra de linha se necessário
   */
  formatarTituloComNomeCliente(prefixo, nomeCliente) {
    const nomeTratado = nomeCliente.trim();
    
    if (nomeTratado.length > 25) {
      return {
        titulo: prefixo,
        subtitulo: nomeTratado,
        duasLinhas: true
      };
    }
    
    this.doc.setFontSize(12);
    this.doc.setFont("helvetica", "bold");
    
    const tituloCompleto = `${prefixo} ${nomeTratado}`;
    const larguraTitulo = this.doc.getStringUnitWidth(tituloCompleto) * 14 / this.doc.internal.scaleFactor;
    
    if (larguraTitulo > (this.pageWidth - this.margin * 2)) {
      return {
        titulo: prefixo,
        subtitulo: nomeTratado,
        duasLinhas: true
      };
    }
    
    return {
      titulo: tituloCompleto,
      subtitulo: null,
      duasLinhas: false
    };
  }

  /**
   * Renderizar Estatísticas Gerais
   */
  renderizarEstatisticasGerais(stats, startY) {
    let currentY = this.verificarEspaco(startY, 80);
    
    this.doc.setFontSize(14);
    this.doc.setFont("helvetica", "bold");
    this.doc.text("Estatísticas Gerais", this.margin, currentY);
    currentY += 12;
    
    const data = [
      ["Total de Fornecedores", stats.totalCadastros || "0"],
      ["Questionários Enviados", stats.questionariosEnviados || "0"],
      ["Questionários Respondidos", stats.questionariosRespondidos || "0"],
      ["Questionários Não Respondidos", stats.questionariosNaoRespondidos || "0"],
      ["Envios Pendentes", stats.enviosPendentes || "0"],
      ["Planos de Ação", stats.planosDeAcao || "0"],
    ];
    
    this.doc.autoTable({
      startY: currentY,
      head: [["Informações", "Quantidade"]],
      body: data,
      theme: 'grid',
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontSize: 11
      },
      styles: {
        cellPadding: 3,
        fontSize: 10
      },
      columnStyles: {
        0: { cellWidth: '70%' },
        1: { cellWidth: '30%', halign: 'center' }
      },
      margin: { left: this.margin, right: this.margin },
      tableWidth: this.pageWidth - (this.margin * 2)
    });
    
    return this.doc.lastAutoTable.finalY + 5;
  }

  /**
   * Renderizar Estatísticas de Tentativas de Envio
   */
  renderizarTentativasEnvio(tentativasData, startY) {
    if (!tentativasData || !tentativasData.stats) {
      return startY;
    }
    
    let currentY = this.verificarEspaco(startY, 40);
    const stats = tentativasData.stats;
    const tentativas = tentativasData.tentativas || [];
    
    this.doc.setFontSize(14);
    this.doc.setFont("helvetica", "bold");
    const tituloTentativas = tentativasData.metadata?.logica_corrigida 
      ? "Estatísticas de Tentativas de Envio"
      : "Estatísticas de Tentativas de Envio";
    
    this.doc.text(tituloTentativas, this.margin, currentY);
    currentY += 10;
    
    this.doc.setFontSize(9);
    this.doc.setFont("helvetica", "italic");
    this.doc.setTextColor(100, 100, 100);
    this.doc.text("* Dados processados", this.margin, currentY);
    this.doc.setTextColor(0, 0, 0);
    currentY += 8;
    
    this.doc.setFontSize(11);
    this.doc.setFont("helvetica", "bold");
    this.doc.text("Resumo:", this.margin, currentY);
    
    this.doc.setFontSize(9);
    this.doc.setFont("helvetica", "normal");
    this.doc.text(`Envios: ${stats.totalGeral.envios} | Respostas: ${stats.totalGeral.respondidos} | Taxa: ${stats.totalGeral.percentualResposta}%`, this.margin + 25, currentY);
    currentY += 10;
    
    const barWidth = this.pageWidth - (this.margin * 2);
    const barHeight = 8;
    
    this.doc.setFillColor(230, 230, 230);
    this.doc.rect(this.margin, currentY, barWidth, barHeight, 'F');
    
    this.doc.setFillColor(24, 144, 255);
    this.doc.rect(this.margin, currentY, barWidth * (stats.totalGeral.percentualResposta / 100), barHeight, 'F');
    
    this.doc.setDrawColor(180, 180, 180);
    this.doc.rect(this.margin, currentY, barWidth, barHeight, 'S');
    
    currentY += barHeight + 6;
    
    currentY = this.verificarEspaco(currentY, 70);
    
    this.doc.setFontSize(11);
    this.doc.setFont("helvetica", "bold");  
    this.doc.text("Detalhamento por Tentativa:", this.margin, currentY);
    currentY += 8;
    
    const headers = ["Tentativa", "Enviados", "Respondidos", "Não Respondidos", "Taxa de Resposta"];
    const data = [
      [
        "1ª Tentativa",
        stats.tentativa1.total.toString(),
        stats.tentativa1.respondidos.toString(),
        stats.tentativa1.naoRespondidos.toString(),
        `${stats.tentativa1.percentualResposta}%`
      ],
      [
        "2ª Tentativa", 
        stats.tentativa2.total.toString(),
        stats.tentativa2.respondidos.toString(),
        stats.tentativa2.naoRespondidos.toString(),
        `${stats.tentativa2.percentualResposta}%`
      ],
      [
        "3ª Tentativa",
        stats.tentativa3.total.toString(),
        stats.tentativa3.respondidos.toString(),
        stats.tentativa3.naoRespondidos.toString(),
        `${stats.tentativa3.percentualResposta}%`
      ]
    ];
    
    this.doc.autoTable({
      startY: currentY,
      head: [headers],
      body: data,
      theme: 'striped',
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: '20%', fontStyle: 'bold' },
        1: { cellWidth: '20%', halign: 'center' },
        2: { cellWidth: '20%', halign: 'center' },
        3: { cellWidth: '20%', halign: 'center' },
        4: { cellWidth: '20%', halign: 'center' }
      },
      margin: { left: this.margin, right: this.margin },
      tableWidth: this.pageWidth - (this.margin * 2),
      styles: {
        cellPadding: 3
      }
    });
    
    currentY = this.doc.lastAutoTable.finalY + 5;
    
    if (tentativas && tentativas.length > 0) {
      currentY = this.renderizarDetalheTentativas(tentativas, currentY);
    }
    
    currentY = this.renderizarAnaliseTentativas(stats, currentY);
    
    return currentY;
  }

  /**
   * Renderizar detalhes das tentativas
   */
  renderizarDetalheTentativas(tentativas, startY) {
    let currentY = this.verificarEspaco(startY, 50);
    
    this.doc.setFontSize(11);
    this.doc.setFont("helvetica", "bold");
    this.doc.text("Detalhes das Tentativas:", this.margin, currentY);
    currentY += 8;
    
    const tentativasFiltradas = tentativas.filter(t => t.tentativa <= 3);
    
    const tentativasAgrupadas = tentativasFiltradas.reduce((acc, tentativa) => {
      if (!acc[tentativa.fornecedorId]) {
        acc[tentativa.fornecedorId] = [];
      }
      acc[tentativa.fornecedorId].push(tentativa);
      return acc;
    }, {});

    const tentativasOrdenadas = [];
    Object.values(tentativasAgrupadas).forEach(fornecedorTentativas => {
      const ordenadas = fornecedorTentativas.sort((a, b) => a.tentativa - b.tentativa);
      tentativasOrdenadas.push(...ordenadas);
    });
    
    const maxTentativas = 25;
    const tentativasLimitadas = tentativasOrdenadas.slice(0, maxTentativas);
    
    if (tentativasOrdenadas.length > maxTentativas) {
      this.doc.setFontSize(9);
      this.doc.setFont("helvetica", "italic");
      this.doc.text(`Exibindo ${maxTentativas} de ${tentativasOrdenadas.length} tentativas.`, this.margin, currentY);
      currentY += 8;
    }
    
    const tentativasHeaders = [
      "Razão Social", 
      "Email", 
      "Data Cadastro",
      "Data Liberação", 
      "Tentativa", 
      "Data Envio", 
      "Status", 
      "Tempo", 
      "Área"
    ];
    
    const tentativasData = tentativasLimitadas.map(t => [
      t.razaoSocial.length > 20 ? t.razaoSocial.substring(0, 17) + '...' : t.razaoSocial,
      t.email.length > 25 ? t.email.substring(0, 22) + '...' : t.email,
      t.dataCadastroFornecedor ? new Date(t.dataCadastroFornecedor).toLocaleDateString('pt-BR') : 'N/A',
      t.dataInicioModulo ? new Date(t.dataInicioModulo).toLocaleDateString('pt-BR') : 'N/A',
      `${t.tentativa}ª`,
      t.dataEnvio ? new Date(t.dataEnvio).toLocaleDateString('pt-BR') : '-',
      this.traduzirStatusResposta(t.statusResposta),
      t.tempoResposta !== null && t.tempoResposta !== undefined ? 
        (t.tempoResposta === 0 ? '0d' : `${t.tempoResposta}d`) : '-',
      t.area.length > 15 ? t.area.substring(0, 12) + '...' : t.area
    ]);
    
    this.doc.autoTable({
      startY: currentY,
      head: [tentativasHeaders],
      body: tentativasData,
      theme: 'striped',
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontSize: 8
      },
      bodyStyles: {
        fontSize: 7
      },
      columnStyles: {
        0: { cellWidth: '16%' },
        1: { cellWidth: '16%' },
        2: { cellWidth: '10%', halign: 'center' },
        3: { cellWidth: '10%', halign: 'center' },
        4: { cellWidth: '7%', halign: 'center' },
        5: { cellWidth: '10%', halign: 'center' },
        6: { cellWidth: '12%', halign: 'center' },
        7: { cellWidth: '7%', halign: 'center' },
        8: { cellWidth: '12%' }
      },
      margin: { left: this.margin, right: this.margin },
      tableWidth: this.pageWidth - (this.margin * 2),
      styles: {
        overflow: 'linebreak',
        cellPadding: 2
      }
    });
    
    return this.doc.lastAutoTable.finalY + 5;
  }

  /**
   * Renderizar análise das tentativas
   */
  renderizarAnaliseTentativas(stats, startY) {
    let currentY = this.verificarEspaco(startY, 30);
    
    this.doc.setFontSize(11);
    this.doc.setFont("helvetica", "bold");
    this.doc.text("Análise dos Resultados:", this.margin, currentY);
    currentY += 8;
    
    this.doc.setFontSize(9);
    this.doc.setFont("helvetica", "normal");
    
    let insights = [];
    
    if (stats.tentativa1.percentualResposta >= 70) {
      insights.push("• Alta taxa na 1ª tentativa indica boa receptividade.");
    } else if (stats.tentativa1.percentualResposta >= 40) {
      insights.push("• Taxa moderada na 1ª tentativa, com potencial de melhoria.");
    } else {
      insights.push("• Baixa taxa na 1ª tentativa sugere revisar comunicação.");
    }
    
    if (stats.tentativa2.total > 0) {
      const proporcao = Math.round((stats.tentativa2.total / stats.totalGeral.envios) * 100);
      insights.push(`• ${proporcao}% precisaram de 2ª tentativa.`);
    }
    
    if (stats.tentativa3.total > 0) {
      const proporcao = Math.round((stats.tentativa3.total / stats.totalGeral.envios) * 100);
      insights.push(`• ${proporcao}% necessitaram de 3ª tentativa.`);
    }
    
    const taxaGeral = stats.totalGeral.percentualResposta;
    if (taxaGeral >= 70) {
      insights.push("• Taxa geral excelente de resposta.");
    } else if (taxaGeral >= 50) {
      insights.push("• Taxa geral boa, com margem para otimização.");
    } else {
      insights.push("• Taxa geral baixa, necessita revisão da estratégia.");
    }
    
    insights.forEach(insight => {
      if (currentY + 50 > this.pageHeight - 45) {
        this.doc.addPage();
        currentY = this.margin;
      }
      this.doc.text(insight, this.margin, currentY);
      currentY += 6;
    });
    
    return currentY;
  }

  /**
   * Renderizar Maturidade
   */
  renderizarMaturidade(maturidade, startY) {
    let currentY = this.verificarEspaco(startY, 60);
    
    this.doc.setFontSize(14);
    this.doc.setFont("helvetica", "bold");
    this.doc.text("Maturidade de Fornecedores", this.margin, currentY);
    currentY += 12;
    
    const percentual = maturidade.percentualMaturidade || 0;
    const nivel = maturidade.nivelConformidade || 'baixo';
    
    this.doc.setFontSize(10);
    this.doc.setFont("helvetica", "normal");
    this.doc.text(`Percentual: ${percentual}% | Nível: ${this.formatarNivel(nivel)}`, this.margin, currentY);
    currentY += 10;
    
    const barWidth = this.pageWidth - (this.margin * 2);
    const barHeight = 10;
    
    this.doc.setFillColor(230, 230, 230);
    this.doc.rect(this.margin, currentY, barWidth, barHeight, 'F');
    
    const corBarra = this.getCorNivel(nivel);
    this.doc.setFillColor(corBarra.r, corBarra.g, corBarra.b);
    this.doc.rect(this.margin, currentY, barWidth * (percentual / 100), barHeight, 'F');
    
    this.doc.setDrawColor(180, 180, 180);
    this.doc.rect(this.margin, currentY, barWidth, barHeight, 'S');
    
    return currentY + barHeight + 8;
  }

  /**
   * Renderizar Estatísticas de Planos de Ação
   */
  renderizarEstatisticasPlanosAcao(planosAcao, startY) {
    if (!planosAcao) return startY;
    
    let currentY = this.verificarEspaco(startY, 50);
    
    this.doc.setFontSize(14);
    this.doc.setFont("helvetica", "bold");
    this.doc.text("Planos de Ação", this.margin, currentY);
    currentY += 12;
    
    if (typeof planosAcao.percentualConcluido === 'number') {
      this.doc.setFontSize(10);
      this.doc.setFont("helvetica", "normal");
      this.doc.text(`Progresso: ${planosAcao.percentualConcluido}% | Respostas: ${planosAcao.respostasObtidas || 0}/${planosAcao.totalFornecedores || 0} | Iniciados: ${planosAcao.planosIniciados || 0}`, this.margin, currentY);
      currentY += 10;
      
      const barWidth = this.pageWidth - (this.margin * 2);
      const barHeight = 10;
      
      this.doc.setFillColor(230, 230, 230);
      this.doc.rect(this.margin, currentY, barWidth, barHeight, 'F');
      
      this.doc.setFillColor(123, 57, 219);
      this.doc.rect(this.margin, currentY, barWidth * (planosAcao.percentualConcluido / 100), barHeight, 'F');
      
      this.doc.setDrawColor(180, 180, 180);
      this.doc.rect(this.margin, currentY, barWidth, barHeight, 'S');
      
      return currentY + barHeight + 8;
    } else {
      this.doc.setFontSize(10);
      this.doc.text("Dados não disponíveis.", this.margin, currentY);
      return currentY + 15;
    }
  }

  /**
  * Renderizar Tabela de Fornecedores
  */
 renderizarTabelaFornecedores(fornecedores, startY) {
   let currentY = this.verificarEspaco(startY, 50);
   
   this.doc.setFontSize(14);
   this.doc.setFont("helvetica", "bold");
   this.doc.text("Lista de Fornecedores", this.margin, currentY);
   currentY += 12;
   
   const headers = [
     "Razão Social", 
     "CNPJ", 
     "Status", 
     "Questionário",
     "Área"
   ];
   
   const data = fornecedores.map(f => [
     f.razaoSocial || "-",
     this.formatarCNPJ(f.cnpj) || "-",
     f.status || "-",
     this.traduzirStatusQuestionario(f.ultimoQuestionario?.status) || "-",
     f.area || "-"
   ]);
   
   if (data.length > 0) {
     this.doc.autoTable({
       startY: currentY,
       head: [headers],
       body: data,
       theme: 'striped',
       headStyles: {
         fillColor: [66, 139, 202],
         textColor: 255,
         fontSize: 9
       },
       styles: {
         cellPadding: 3,
         fontSize: 8,
         overflow: 'linebreak'
       },
       columnStyles: {
         0: { cellWidth: '30%' },
         1: { cellWidth: '20%' },
         2: { cellWidth: '15%' },
         3: { cellWidth: '20%' },
         4: { cellWidth: '15%' }
       },
       margin: { left: this.margin, right: this.margin },
       tableWidth: this.pageWidth - (this.margin * 2)
     });
   } else {
     this.doc.setFontSize(10);
     this.doc.setFont("helvetica", "normal");
     this.doc.text('Nenhum fornecedor encontrado com os filtros atuais.', this.margin, currentY);
   }
 }

 /**
  * Gera um relatório detalhado de um fornecedor específico
  */
 async gerarRelatorioFornecedor(clienteId, fornecedorId, filtros = {}) {
   try {
     let nomeCliente = this.obterNomeCliente(filtros);
     
     const [fornecedorResponse, questionariosResponse, maturidadeResponse, timelineResponse, planoAcaoResponse] = 
      await this.buscarDadosFornecedor(clienteId, fornecedorId, filtros);
    
    const fornecedor = fornecedorResponse.data;
    const questionarios = Array.isArray(questionariosResponse.data) ? 
                          questionariosResponse.data : 
                          (questionariosResponse.data ? [questionariosResponse.data] : []);
    const maturidade = maturidadeResponse.data;
    const timelineData = timelineResponse.data;
    const planoAcao = planoAcaoResponse.data;
    
    if (!fornecedor) {
      throw new Error('Dados do fornecedor não encontrados');
    }
    
    const nomeFornecedor = fornecedor.razaoSocial || fornecedor.razao_social || "Não identificado";
    
    this.doc = new jsPDF();
    
    let currentY = this.criarCabecalho(nomeCliente);
    
    this.doc.setFontSize(12);
    this.doc.setFont("helvetica", "bold");
    this.doc.text(`Relatório de Fornecedor: ${nomeFornecedor}`, this.margin, currentY);
    currentY += 15;
    
    currentY = this.renderizarInfoCliente(nomeCliente, filtros, currentY);
    currentY += 5;
    
    if (filtros.opcoes?.incluirInfoFornecedor !== false) {
      currentY = this.renderizarInfoFornecedor(fornecedor, currentY);
      currentY += 5;
    }
    
    if (maturidade && filtros.opcoes?.incluirMaturidade !== false) {
      currentY = this.renderizarMaturidadeFornecedor(maturidade, currentY);
      currentY += 5;
    }
    
    if (timelineData && filtros.opcoes?.incluirTimeline !== false) {
      currentY = this.renderizarTimelineFornecedor(timelineData, currentY);
      currentY += 5;
    }
    
    if (questionarios && questionarios.length > 0 && filtros.opcoes?.incluirQuestionarios !== false) {
      currentY = this.renderizarQuestionariosFornecedor(questionarios, currentY);
      currentY += 5;
    }
    
    if (planoAcao && planoAcao.dataPlanoAcao && filtros.opcoes?.incluirPlanosDeAcao !== false) {
      this.renderizarPlanoAcaoFornecedor(planoAcao, currentY);
    }
    
    this.adicionarRodape();
    
    return this.doc;
  } catch (error) {
    throw new Error('Falha ao gerar relatório PDF. ' + (error.response?.data?.message || error.message));
  }
}

/**
  * Buscar dados do fornecedor
  */
 async buscarDadosFornecedor(clienteId, fornecedorId, filtros) {
   const fornecedorPromise = api.get(`/dashboard/governanca/fornecedores/fornecedor/${fornecedorId}`, {
     params: { cliente_id: clienteId }
   });
   
   const questionariosPromise = api.get(`/dashboard/governanca/fornecedores/questionarios/fornecedor/${fornecedorId}`, {
     params: { 
       cliente_id: clienteId,
       data_inicio: filtros.dataInicio,
       data_fim: filtros.dataFim
     }
   });
   
   const maturidadePromise = api.get('/dashboard/governanca/fornecedores/maturidade', {
     params: { 
       cliente_id: clienteId,
       fornecedor_id: fornecedorId
     }
   });
   
   const timelinePromise = filtros.opcoes?.incluirTimeline !== false 
     ? api.get(`/dashboard/governanca/fornecedores/timeline`, {
         params: { 
           cliente_id: clienteId, 
           fornecedor_id: fornecedorId,
           incluir_emails: true
         }
       }).catch(() => ({ data: null }))
     : Promise.resolve({ data: null });
   
   const planoAcaoPromise = filtros.opcoes?.incluirPlanosDeAcao !== false
     ? api.get('/dashboard/governanca/fornecedores/planos-acao', {
         params: { 
           cliente_id: clienteId,
           fornecedor_id: fornecedorId 
         }
       }).catch(() => ({ data: null }))
     : Promise.resolve({ data: null });
   
   return await Promise.all([fornecedorPromise, questionariosPromise, maturidadePromise, timelinePromise, planoAcaoPromise]);
 }

/**
 * Renderizar informações do cliente
 */
renderizarInfoCliente(nomeCliente, filtros, startY) {
  let currentY = this.verificarEspaco(startY, 60);
  
  this.doc.setFontSize(12);
  this.doc.setFont("helvetica", "bold");
  this.doc.text("Informações do Cliente", this.margin, currentY);
  currentY += 10;
  
  const clienteData = [
    ["Nome/Razão Social", nomeCliente || "-"]
  ];

  try {
    const currentClienteString = localStorage.getItem('currentCliente');
    if (currentClienteString) {
      const dadosClienteAdicionais = JSON.parse(currentClienteString);
      if (dadosClienteAdicionais) {
        if (dadosClienteAdicionais.cnpj) {
          clienteData.push(["CNPJ", this.formatarCNPJ(dadosClienteAdicionais.cnpj) || "-"]);
        }
        if (dadosClienteAdicionais.email) {
          clienteData.push(["E-mail", dadosClienteAdicionais.email || "-"]);
        }
      }
    }
  } catch (error) {
    // Falha silenciosa ao obter dados do cliente
  }
  
  this.doc.autoTable({
    startY: currentY,
    body: clienteData,
    theme: 'plain',
    styles: {
      cellPadding: 3,
      fontSize: 9
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: this.pageWidth - this.margin * 2 - 60 }
    },
    margin: { left: this.margin, right: this.margin },
    tableWidth: this.pageWidth - (this.margin * 2)
  });
  
  return this.doc.lastAutoTable.finalY + 5;
}

/**
 * Renderizar informações do fornecedor
 */
renderizarInfoFornecedor(fornecedor, startY) {
  let currentY = this.verificarEspaco(startY, 60);
  
  this.doc.setFontSize(12);
  this.doc.setFont("helvetica", "bold");
  this.doc.text("Informações do Fornecedor", this.margin, currentY);
  currentY += 10;
  
  const data = [
    ["Razão Social", fornecedor.razaoSocial || fornecedor.razao_social || "-"],
    ["CNPJ", this.formatarCNPJ(fornecedor.cnpj) || "-"]
  ];
  
  if (fornecedor.email) data.push(["E-mail", fornecedor.email]);
  if (fornecedor.telefone) data.push(["Telefone", fornecedor.telefone]);
  if (fornecedor.area) data.push(["Área", fornecedor.area]);
  if (fornecedor.status) data.push(["Status", fornecedor.status]);
  if (fornecedor.representante) data.push(["Representante", fornecedor.representante]);
  if (fornecedor.ultimoQuestionario) {
    data.push(["Status do Questionário", this.traduzirStatusQuestionario(fornecedor.ultimoQuestionario?.status) || "-"]);
  }
  
  this.doc.autoTable({
    startY: currentY,
    body: data,
    theme: 'plain',
    styles: {
      cellPadding: 3,
      fontSize: 9
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: this.pageWidth - this.margin * 2 - 60 }
    },
    margin: { left: this.margin, right: this.margin },
    tableWidth: this.pageWidth - (this.margin * 2)
  });
  
  return this.doc.lastAutoTable.finalY + 5;
}

/**
 * Renderizar maturidade do fornecedor
 */
renderizarMaturidadeFornecedor(maturidade, startY) {
  let currentY = this.verificarEspaco(startY, 50);
  
  this.doc.setFontSize(12);
  this.doc.setFont("helvetica", "bold");
  this.doc.text("Avaliação de Maturidade", this.margin, currentY);
  currentY += 10;
  
  const percentual = maturidade.percentualMaturidade || 0;
  const nivel = maturidade.nivelConformidade || 'baixo';
  
  this.doc.setFontSize(10);
  this.doc.setFont("helvetica", "normal");
  this.doc.text(`Percentual: ${percentual}% | Nível: ${this.formatarNivel(nivel)}`, this.margin, currentY);
  currentY += 8;
  
  const barWidth = this.pageWidth - (this.margin * 2);
  const barHeight = 10;
  
  this.doc.setFillColor(230, 230, 230);
  this.doc.rect(this.margin, currentY, barWidth, barHeight, 'F');
  
  const corBarra = this.getCorNivel(nivel);
  this.doc.setFillColor(corBarra.r, corBarra.g, corBarra.b);
  this.doc.rect(this.margin, currentY, barWidth * (percentual / 100), barHeight, 'F');
  
  this.doc.setDrawColor(180, 180, 180);
  this.doc.rect(this.margin, currentY, barWidth, barHeight, 'S');
  
  currentY += barHeight + 8;
  
  let descricao = '';
  if (nivel === 'alto') {
    descricao = 'Alta conformidade com requisitos avaliados.';
  } else if (nivel === 'medio') {
    descricao = 'Conformidade média com oportunidades de melhoria.';
  } else {
    descricao = 'Baixa conformidade, necessita ações corretivas.';
  }
  
  this.doc.setFontSize(9);
  this.doc.text(descricao, this.margin, currentY);
  
  return currentY + 10;
}

/**
 * Renderizar timeline do fornecedor
 */
renderizarTimelineFornecedor(timelineData, startY) {
  let currentY = this.verificarEspaco(startY, 60);
  
  this.doc.setFontSize(12);
  this.doc.setFont("helvetica", "bold");
  this.doc.text("Linha do Tempo", this.margin, currentY);
  currentY += 10;
  
  const eventos = [];
  
  if (timelineData.cadastroFornecedor) {
    eventos.push({
      data: new Date(timelineData.cadastroFornecedor),
      tipo: 'Cadastro',
      descricao: 'Fornecedor cadastrado'
    });
  }
  
  if (timelineData.aplicacoesQuestionarios && timelineData.aplicacoesQuestionarios.length > 0) {
    timelineData.aplicacoesQuestionarios.forEach(e => {
      eventos.push({
        data: new Date(e.data),
        tipo: 'Questionário',
        descricao: `Aplicação: ${e.titulo || 'Questionário'}`
      });
    });
  }
  
  if (timelineData.respostasQuestionarios && timelineData.respostasQuestionarios.length > 0) {
    timelineData.respostasQuestionarios.forEach(e => {
      eventos.push({
        data: new Date(e.data),
        tipo: 'Resposta',
        descricao: `Questionário respondido`
      });
    });
  }
  
  eventos.sort((a, b) => a.data.getTime() - b.data.getTime());
  
  if (eventos.length > 0) {
    const eventosFormatados = eventos.map(e => [
      e.data.toLocaleDateString('pt-BR'),
      e.tipo,
      e.descricao
    ]);
    
    this.doc.autoTable({
      startY: currentY,
      head: [['Data', 'Tipo', 'Descrição']],
      body: eventosFormatados,
      theme: 'striped',
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontSize: 9
      },
      styles: {
        cellPadding: 3,
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: '20%' },
        1: { cellWidth: '20%' },
        2: { cellWidth: '60%' }
      },
      margin: { left: this.margin, right: this.margin },
      tableWidth: this.pageWidth - (this.margin * 2)
    });
    
    return this.doc.lastAutoTable.finalY + 5;
  } else {
    this.doc.setFontSize(9);
    this.doc.text("Nenhum evento disponível.", this.margin, currentY);
    return currentY + 15;
  }
}

/**
  * Renderizar questionários do fornecedor
  */
 renderizarQuestionariosFornecedor(questionarios, startY) {
   let currentY = this.verificarEspaco(startY, 60);
   
   this.doc.setFontSize(12);
   this.doc.setFont("helvetica", "bold");
   this.doc.text("Questionários Respondidos", this.margin, currentY);
   currentY += 10;
   
   const mapQuestoes = {
     "64ee165a41736da1927a46aa": "Porte da empresa",
     "64ee165a41736da1927a46ab": "Ramo de atuação",
     "64ee165a41736da1927a46af": "Possui DPO designado",
     "64ee165a41736da1927a46b0": "Políticas de privacidade",
     "64ee165a41736da1927a46b1": "Tratamento de dados pessoais",
     "64ee165a41736da1927a46b2": "Controles de segurança",
     "64ee165a41736da1927a46b4": "Avaliação de riscos",
     "64ee165a41736da1927a46b5": "Procedimentos para incidentes",
     "64ee165a41736da1927a46b6": "Treinamentos sobre proteção",
     "64ee165a41736da1927a46b7": "Contratos com terceiros",
     "660fee5de65c9279bff20fa8": "Questão sobre conformidade",
     "64ee165a41736da1927a46bb": "Possui sistema de gestão de incidentes",
     "64ee165a41736da1927a46bc": "Canal de comunicação para solicitações",
     "64ee165a41736da1927a46bd": "Realiza avaliação de impacto",
     "64ee165a41736da1927a46be": "Auditoria interna ou externa",
     "64ee165a41736da1927a46c0": "Possui política de backup",
     "64ee165a41736da1927a46c1": "Tipo de auditoria realizada",
     "64ee165a41736da1927a46cc": "Possui controle de acesso",
     "64ee165a41736da1927a46cd": "Frequência de revisão de acessos",
     "64ee165a41736da1927a46c7": "Ferramentas de segurança utilizadas",
     "64ee165a41736da1927a46c6": "Possui monitoramento de segurança",
     "64ee165a41736da1927a46c8": "Especificação das ferramentas de backup",
     "64ee165a41736da1927a46c4": "Utiliza computação em nuvem",
     "64ee165a41736da1927a46d0": "Local de armazenamento dos dados",
     "64ee165a41736da1927a46d1": "Controle de acesso físico",
     "64ee165a41736da1927a46ce": "Tempo de implementação das medidas",
     "64ee165a41736da1927a46d3": "Medidas de segurança específicas",
     "64ee165a41736da1927a46cf": "Possui certificações de segurança",
     "65132a0ec6ae5654cc6ce904": "Realiza testes de segurança",
     "65132b2ac6ae5654cc6ce905": "Frequência dos testes de segurança"
   };
   
   questionarios.forEach((questionario, index) => {
     currentY = this.verificarEspaco(currentY, 40);
     
     this.doc.setFontSize(11);
     this.doc.setFont("helvetica", "bold");
     
     const titulo = questionario.titulo || `Questionário ${index + 1}`;
     this.doc.text(titulo, this.margin, currentY);
     currentY += 8;
     
     let dataResposta = 'Data não disponível';
     
     if (questionario.data_resposta) {
       try {
         dataResposta = new Date(questionario.data_resposta).toLocaleDateString('pt-BR');
       } catch (error) {
       }
     } else if (questionario.updated_at) {
       try {
         dataResposta = new Date(questionario.updated_at).toLocaleDateString('pt-BR');
       } catch (error) {
       }
     } else if (questionario.created_at) {
       try {
         dataResposta = new Date(questionario.created_at).toLocaleDateString('pt-BR');
       } catch (error) {
       }
     }
     
     this.doc.setFontSize(9);
     this.doc.setFont("helvetica", "normal");
     this.doc.text(`Respondido em: ${dataResposta}`, this.margin, currentY);
     currentY += 8;
     
     if (questionario.respostas && questionario.respostas.length > 0) {
       const headers = ["Pergunta", "Resposta"];
       const data = questionario.respostas.map(r => {
         let textoPergunta = '';
         
         if (r.pergunta && r.pergunta.trim()) {
           textoPergunta = r.pergunta;
         }
         else if (r.texto && r.texto.trim()) {
           textoPergunta = r.texto;
         }
         else if (r.questao_id && mapQuestoes[r.questao_id]) {
           textoPergunta = mapQuestoes[r.questao_id];
         }
         else if (r.questao_id) {
           textoPergunta = `Questão ${r.questao_id.substring(0, 8)}...`;
         }
         else {
           textoPergunta = 'Pergunta não identificada';
         }
         
         let textoResposta = '';
         if (Array.isArray(r.resposta)) {
           textoResposta = r.resposta.join(', ');
         } else if (r.resposta !== undefined && r.resposta !== null) {
           textoResposta = String(r.resposta);
         } else {
           textoResposta = '-';
         }
         
         return [textoPergunta, textoResposta];
       });
       
       const larguraTabela = this.pageWidth - (this.margin * 2);
       
       this.doc.autoTable({
         startY: currentY,
         head: [headers],
         body: data,
         theme: 'grid',
         headStyles: {
           fillColor: [66, 139, 202],
           textColor: 255,
           fontSize: 8,
           fontStyle: 'bold',
           halign: 'center',
           valign: 'middle'
         },
         styles: {
           overflow: 'linebreak',
           cellPadding: 3,
           fontSize: 8,
           halign: 'left',
           valign: 'top',
           lineColor: [200, 200, 200],
           lineWidth: 0.1
         },
         columnStyles: {
           0: { 
             cellWidth: larguraTabela * 0.35,
             overflow: 'linebreak',
             fontStyle: 'normal'
           },
           1: { 
             cellWidth: larguraTabela * 0.65,
             overflow: 'linebreak',
             fontStyle: 'normal'
           }
         },
         tableWidth: larguraTabela,
         margin: { 
           left: this.margin, 
           right: this.margin 
         },
         tableLineColor: [200, 200, 200],
         tableLineWidth: 0.1,
         alternateRowStyles: {
           fillColor: [249, 249, 249]
         }
       });
       
       currentY = this.doc.lastAutoTable.finalY + 10;
     } else {
       this.doc.text("Nenhuma resposta disponível.", this.margin, currentY);
       currentY += 15;
     }
   });
   
   return currentY;
}

async buscarTodasQuestoes() {
  try {
    const response = await api.get('/questoes');
    const questoes = response.data;
    
    const mapCompleto = {};
    questoes.forEach(questao => {
      if (questao._id && (questao.texto || questao.questao)) {
        mapCompleto[questao._id] = questao.texto || questao.questao;
      }
    });
    
    return mapCompleto;
  } catch (error) {
    console.error('Erro ao buscar questões:', error);
    return {};
  }
}

/**
 * Renderizar plano de ação do fornecedor
 */
renderizarPlanoAcaoFornecedor(planoAcao, startY) {
  let currentY = this.verificarEspaco(startY, 60);
  
  this.doc.setFontSize(12);
  this.doc.setFont("helvetica", "bold");
  this.doc.text("Plano de Ação", this.margin, currentY);
  currentY += 10;
  
  this.doc.setFontSize(10);
  this.doc.setFont("helvetica", "normal");
  this.doc.text(`Status: ${planoAcao.statusAcao || 'Não definido'}`, this.margin, currentY);
  currentY += 8;
  
  if (planoAcao.dataPlanoAcao) {
    const dataFormatada = new Date(planoAcao.dataPlanoAcao).toLocaleDateString('pt-BR');
    this.doc.text(`Aplicado em: ${dataFormatada}`, this.margin, currentY);
    currentY += 10;
  }
  
  if (planoAcao.acoes && planoAcao.acoes.length > 0) {
    const headers = ["Ação", "Responsável", "Prazo", "Status"];
    const data = planoAcao.acoes.map(acao => [
      acao.descricao || '-',
      acao.responsavel || '-',
      acao.prazo ? new Date(acao.prazo).toLocaleDateString('pt-BR') : '-',
      acao.status || '-'
    ]);
    
    this.doc.autoTable({
      startY: currentY,
      head: [headers],
      body: data,
      theme: 'striped',
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontSize: 9
      },
      styles: {
        overflow: 'linebreak',
        cellPadding: 3,
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: '40%' },
        1: { cellWidth: '20%' },
        2: { cellWidth: '20%' },
        3: { cellWidth: '20%' }
      },
      margin: { left: this.margin, right: this.margin },
      tableWidth: this.pageWidth - (this.margin * 2)
    });
    
    return this.doc.lastAutoTable.finalY + 5;
  } else {
    this.doc.text("Nenhuma ação definida.", this.margin, currentY);
    return currentY + 15;
  }
}

/**
 * Adiciona o cabeçalho ao documento PDF
 */
adicionarCabecalho(titulo) {
  this.doc.setFontSize(18);
  this.doc.setFont("helvetica", "bold");
  this.doc.text(titulo, this.pageWidth / 2, this.margin + 10, { align: "center" });

  const dataAtual = new Date().toLocaleDateString("pt-BR");
  const horaAtual = new Date().toLocaleTimeString("pt-BR");
  
  this.doc.setFontSize(10);
  this.doc.setFont("helvetica", "normal");
  this.doc.text(`Gerado em: ${dataAtual} - ${horaAtual}`, this.pageWidth - this.margin, this.margin + 20, { align: "right" });
  
  this.doc.setDrawColor(0, 0, 0);
  this.doc.line(this.margin, this.margin + 25, this.pageWidth - this.margin, this.margin + 25);
}

/**
 * Adiciona informações sobre os filtros aplicados
 */
adicionarInformacoesFiltros(filtros) {
  if (!filtros || Object.keys(filtros).length === 0) return;
  
  const finalY = this.margin + 20;
  
  this.doc.setFontSize(9);
  this.doc.setFont("helvetica", "italic");
  this.doc.setTextColor(100, 100, 100);
  
  let filtrosTexto = ['Filtros:'];
  
  if (filtros.dataInicio) {
    filtrosTexto.push(`Início: ${new Date(filtros.dataInicio).toLocaleDateString('pt-BR')}`);
  }
  
  if (filtros.dataFim) {
    filtrosTexto.push(`Fim: ${new Date(filtros.dataFim).toLocaleDateString('pt-BR')}`);
  }
  
  if (filtros.search) {
    filtrosTexto.push(`Busca: "${filtros.search}"`);
  }
  
  if (filtros.status && filtros.status.length > 0) {
    filtrosTexto.push(`Status: ${filtros.status.join(', ')}`);
  }
  
  this.doc.text(filtrosTexto.join(' | '), this.margin, finalY + 10);
  this.doc.setTextColor(0, 0, 0);
}

/**
 * Adiciona rodapé a todas as páginas
 */
adicionarRodape() {
  const totalPages = this.doc.internal.getNumberOfPages();
  
  for (let i = 1; i <= totalPages; i++) {
    this.doc.setPage(i);
    
    this.doc.setDrawColor(200, 200, 200);
    this.doc.line(this.margin, this.pageHeight - 20, this.pageWidth - this.margin, this.pageHeight - 20);
    
    this.doc.setFontSize(8);
    this.doc.setTextColor(150, 150, 150);
    this.doc.text(
      `Página ${i} de ${totalPages}`, 
      this.pageWidth / 2, 
      this.pageHeight - 10, 
      { align: "center" }
    );
    
    this.doc.text(
      "Seusdados - Jornada de Conformidade", 
      this.margin, 
      this.pageHeight - 10
    );
    
    this.doc.text(
      `Relatório gerado em ${new Date().toLocaleDateString("pt-BR")}`, 
      this.pageWidth - this.margin, 
      this.pageHeight - 10, 
      { align: "right" }
    );
  }
}

/**
 * Baixa o PDF com o nome especificado
 */
salvar(documento, filename = "relatorio-fornecedores.pdf") {
  documento.save(filename);
}

/**
 * Visualiza o PDF em uma nova janela
 */
visualizar(documento) {
  documento.output('dataurlnewwindow');
}

/**
 * Gera um relatório e salva como arquivo PDF
 */
async gerarEBaixar(tipo, params, filename) {
  try {
    let doc;
    
    if (tipo === 'dashboard') {
      doc = await this.gerarRelatorioDashboard(params.clienteId, params.filtros);
      this.salvar(doc, filename || 'relatorio-fornecedores.pdf');
    } else if (tipo === 'fornecedor') {
      doc = await this.gerarRelatorioFornecedor(params.clienteId, params.fornecedorId, params.filtros);
      this.salvar(doc, filename || `relatorio-fornecedor-${params.fornecedorId}.pdf`);
    }
    
    return true;
  } catch (error) {
    throw error;
  }
}

/**
 * Gera um relatório e abre para visualização
 */
async gerarEVisualizar(tipo, params) {
  try {
    let doc;
    
    if (tipo === 'dashboard') {
      doc = await this.gerarRelatorioDashboard(params.clienteId, params.filtros);
    } else if (tipo === 'fornecedor') {
      doc = await this.gerarRelatorioFornecedor(params.clienteId, params.fornecedorId, params.filtros);
    }
    
    this.visualizar(doc);
    return true;
  } catch (error) {
    throw error;
  }
}

/**
 * Gera um relatório e retorna uma URL para visualização
 */
async gerarUrl(tipo, params) {
  try {
    let doc;
    
    if (tipo === 'dashboard') {
      doc = await this.gerarRelatorioDashboard(params.clienteId, params.filtros);
    } else if (tipo === 'fornecedor') {
      doc = await this.gerarRelatorioFornecedor(params.clienteId, params.fornecedorId, params.filtros);
    } else {
      throw new Error('Tipo de relatório inválido');
    }
    
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    return pdfUrl;
  } catch (error) {
    throw error;
  }
}

/**
 * Gera relatório específico de tentativas de envio
 */
async gerarRelatorioTentativas(clienteId, filtros = {}) {
  try {
    let nomeCliente = this.obterNomeCliente(filtros);
    
    const tentativasResponse = await api.get('/dashboard/governanca/fornecedores/tentativas-envio', {
      params: { cliente_id: clienteId }
    });
    
    if (!tentativasResponse || !tentativasResponse.data) {
      throw new Error('Dados de tentativas não encontrados');
    }
    
    const tentativasData = tentativasResponse.data;
    
    this.doc = new jsPDF();
    
    let currentY = this.criarCabecalho(`Relatório de Tentativas de Envio: ${nomeCliente}`);
    
    currentY = this.renderizarTentativasEnvio(tentativasData, currentY);
    
    if (filtros.opcoes?.incluirDetalhesCompletos !== false && tentativasData.tentativas && tentativasData.tentativas.length > 0) {
      currentY = this.verificarEspaco(currentY, 60);
      
      this.doc.setFontSize(12);
      this.doc.setFont("helvetica", "bold");
      this.doc.text("Detalhamento Completo das Tentativas", this.margin, currentY);
      currentY += 12;
      
      let tentativasFiltradas = tentativasData.tentativas;
      if (filtros.tentativaFiltro && filtros.tentativaFiltro !== 'todas') {
        const numeroTentativa = parseInt(filtros.tentativaFiltro);
        tentativasFiltradas = tentativasData.tentativas.filter(t => t.tentativa === numeroTentativa);
      }
      
      const maxRegistros = 40;
      if (tentativasFiltradas.length > maxRegistros) {
        this.doc.setFontSize(9);
        this.doc.setFont("helvetica", "italic");
        this.doc.text(`Exibindo ${maxRegistros} de ${tentativasFiltradas.length} tentativas.`, this.margin, currentY);
        currentY += 8;
        tentativasFiltradas = tentativasFiltradas.slice(0, maxRegistros);
      }
      
      const headers = ["Fornecedor", "CNPJ", "Email", "Tentativa", "Data Envio", "Status", "Tempo", "Área"];
      const data = tentativasFiltradas.map(t => [
        t.razaoSocial.length > 25 ? t.razaoSocial.substring(0, 22) + '...' : t.razaoSocial,
        this.formatarCNPJ(t.cnpj) || '-',
        t.email.length > 30 ? t.email.substring(0, 27) + '...' : t.email,
        `${t.tentativa}ª`,
        t.dataEnvio ? new Date(t.dataEnvio).toLocaleDateString('pt-BR') : '-',
        this.traduzirStatusResposta(t.statusResposta),
        t.tempoResposta !== null && t.tempoResposta !== undefined ? 
          (t.tempoResposta === 0 ? '0d' : `${t.tempoResposta}d`) : '-',
        t.area.length > 15 ? t.area.substring(0, 12) + '...' : t.area
      ]);
      
      this.doc.autoTable({
        startY: currentY,
        head: [headers],
        body: data,
        theme: 'striped',
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontSize: 8
        },
        bodyStyles: {
          fontSize: 7
        },
        columnStyles: {
          0: { cellWidth: '18%' },
          1: { cellWidth: '14%' },
          2: { cellWidth: '18%' },
          3: { cellWidth: '8%', halign: 'center' },
          4: { cellWidth: '12%', halign: 'center' },
          5: { cellWidth: '12%', halign: 'center' },
          6: { cellWidth: '8%', halign: 'center' },
          7: { cellWidth: '10%' }
        },
        margin: { left: this.margin, right: this.margin },
        tableWidth: this.pageWidth - (this.margin * 2)
      });
    }
    
    this.adicionarRodape();
    
    return this.doc;
  } catch (error) {
    throw new Error('Falha ao gerar relatório de tentativas. ' + (error.response?.data?.message || error.message));
  }
}

// Funções auxiliares

/**
 * Formata CNPJ para exibição
 */
formatarCNPJ(cnpj) {
  if (!cnpj) return '-';
  
  const numeros = cnpj.replace(/\D/g, '');
  
  if (numeros.length === 14) {
    return numeros.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    );
  }
  
  return cnpj;
}

/**
 * Traduz status do questionário para exibição
 */
traduzirStatusQuestionario(status) {
  if (!status) return 'Pendente';
  
  const statusMap = {
    'enviado': 'Enviado',
    'respondido': 'Respondido',
    'em_analise': 'Em Análise',
    'pendente': 'Pendente'
  };
  
  return statusMap[status.toLowerCase()] || status;
}

/**
 * Traduz status de resposta para exibição
 */
traduzirStatusResposta(status) {
  const statusMap = {
    'respondido': 'Respondido',
    'em_analise': 'Em Análise',
    'nao_respondido': 'Não Respondido'
  };
  
  return statusMap[status] || status;
}

/**
 * Formata o nível de conformidade para exibição
 */
formatarNivel(nivel) {
  if (!nivel) return 'Baixo';
  
  const nivelMap = {
    'baixo': 'Baixo',
    'medio': 'Médio',
    'alto': 'Alto'
  };
  
  return nivelMap[nivel.toLowerCase()] || nivel;
}

/**
 * Retorna a cor RGB para o nível de conformidade
 */
getCorNivel(nivel) {
  if (!nivel) return { r: 255, g: 0, b: 0 };
  
  const nivelLower = nivel.toLowerCase();
  
  if (nivelLower === 'alto') {
    return { r: 46, g: 204, b: 113 };
  } else if (nivelLower === 'medio') {
    return { r: 243, g: 156, b: 18 };
  } else {
    return { r: 231, g: 76, b: 60 };
  }
}
}

export default new PDFGenerator();